/**
 * WebSocket message protocol, validated with Zod in both directions. Shared by
 * the Durable Object (server) and the React client so the two can never drift.
 */
import { z } from "zod";
import { BOARD_SIZE } from "./constants.js";

const coord = z
  .number()
  .int()
  .min(0)
  .max(BOARD_SIZE - 1);
export const teamSchema = z.enum(["red", "blue"]);
export const chatTextSchema = z.string().trim().min(1).max(300);

/* ------------------------------- client → server ------------------------------- */

export const clientMessageSchema = z.discriminatedUnion("type", [
  // Sent right after the socket opens to (re)hydrate and take a seat.
  z.object({ type: z.literal("hello") }),
  z.object({ type: z.literal("move"), x: coord, y: coord }),
  z.object({ type: z.literal("bomb"), x: coord, y: coord }),
  z.object({ type: z.literal("chat"), text: chatTextSchema }),
  z.object({ type: z.literal("rematch") }),
  // Voluntarily forfeit the active game (opponent wins).
  z.object({ type: z.literal("resign") }),
  z.object({ type: z.literal("leave") }),
  z.object({ type: z.literal("ping") }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/* ------------------------------- server → client ------------------------------- */

const clientCellSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("hidden") }),
  z.object({ state: z.literal("safe"), adjacent: z.number().int().min(0).max(8), owner: teamSchema }),
  z.object({ state: z.literal("mine"), owner: teamSchema }),
]);

export const chatMessageSchema = z.object({
  id: z.string(),
  playerId: z.string(),
  team: teamSchema.nullable(),
  nickname: z.string(),
  text: z.string(),
  at: z.number(),
});

export const playerSeatSchema = z.object({
  playerId: z.string(),
  nickname: z.string(),
  team: teamSchema,
  connected: z.boolean(),
  isAi: z.boolean(),
});

export const gameSnapshotSchema = z.object({
  id: z.string(),
  status: z.enum(["waiting", "ready", "active", "finished", "abandoned"]),
  visibility: z.enum(["public", "private"]),
  joinCode: z.string(),
  cells: z.array(clientCellSchema),
  scores: z.object({ red: z.number(), blue: z.number() }),
  turn: teamSchema,
  winner: teamSchema.nullable(),
  minesRemaining: z.number(),
  bombsAvailable: z.object({ red: z.boolean(), blue: z.boolean() }),
  players: z.array(playerSeatSchema),
  you: teamSchema.nullable(),
  chat: z.array(chatMessageSchema),
});

/** One revealed/claimed cell in an incremental update. */
const revealCellSchema = z.object({
  i: z.number().int(),
  cell: clientCellSchema,
});

export const serverMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("snapshot"), game: gameSnapshotSchema }),
  z.object({
    type: z.literal("reveal"),
    by: teamSchema,
    /** Index of the tapped cell (move) or 3x3 centre (bomb) — lets clients
     * mark where the last move happened, distinct from the full set of
     * cells it revealed via flood-fill/bomb. */
    origin: z.number().int(),
    cells: z.array(revealCellSchema),
    scores: z.object({ red: z.number(), blue: z.number() }),
    turn: teamSchema,
    minesRemaining: z.number(),
    bombsAvailable: z.object({ red: z.boolean(), blue: z.boolean() }),
    mineHit: z.boolean(),
  }),
  z.object({ type: z.literal("chat"), message: chatMessageSchema }),
  z.object({
    type: z.literal("presence"),
    players: z.array(playerSeatSchema),
    status: z.enum(["waiting", "ready", "active", "finished", "abandoned"]),
  }),
  z.object({
    type: z.literal("gameOver"),
    winner: teamSchema.nullable(),
    scores: z.object({ red: z.number(), blue: z.number() }),
    reason: z.enum(["normal", "forfeit"]).optional(),
  }),
  // A player has asked for a rematch; the game restarts once both agree.
  z.object({ type: z.literal("rematchPending"), by: teamSchema }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
  z.object({ type: z.literal("pong") }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

export function parseClientMessage(raw: unknown): ClientMessage | null {
  const result = clientMessageSchema.safeParse(raw);
  return result.success ? result.data : null;
}

export function parseServerMessage(raw: unknown): ServerMessage | null {
  const result = serverMessageSchema.safeParse(raw);
  return result.success ? result.data : null;
}
